import React, { useCallback, useState } from "react";
import { Modal, Form, Input, Button } from "semantic-ui-react";
import styles from "./nextLinkModal.module.scss";
import ModalHeader from "components/modal/header/ModalHeader";
import * as yup from "yup";

interface NextLinkModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (url: string, displayName: string) => void;
  initialValue?: string;
  initialDisplayName?: string;
}

const urlSchema = yup.string().url("Please enter a valid URL (e.g. https://example.com)").required("URL is required.");

const displayNameSchema = yup
  .string()
  .required("Display name is required.")
  .max(128, "Display name cannot exceed 128 characters.");

const NextLinkModal: React.FC<NextLinkModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialValue = "",
  initialDisplayName = "",
}) => {
  const [url, setUrl] = useState(initialValue);
  const [displayName, setDisplayName] = useState(initialDisplayName);
  const [urlError, setUrlError] = useState("");
  const [displayNameError, setDisplayNameError] = useState("");

  const handleSave = useCallback(async () => {
    const currentValue = url.trim();
    const currentDisplayName = displayName.trim();
    const doesNotStartWithHttp =
      currentValue && !(currentValue.startsWith("http://") || currentValue.startsWith("https://"));
    const urlToValidate = doesNotStartWithHttp ? `http://${currentValue}` : currentValue;

    let isValid = true;

    // Validate URL
    try {
      await urlSchema.validate(urlToValidate);
      setUrlError(""); // Clear any previous error
    } catch (validationError) {
      isValid = false;
      if (validationError instanceof yup.ValidationError) {
        setUrlError(validationError.message);
      } else {
        setUrlError("An unexpected validation error occurred.");
      }
    }

    // Validate display name
    try {
      await displayNameSchema.validate(currentDisplayName);
      setDisplayNameError("");
    } catch (validationError) {
      isValid = false;
      if (validationError instanceof yup.ValidationError) {
        setDisplayNameError(validationError.message);
      } else {
        setDisplayNameError("An unexpected validation error occurred.");
      }
    }

    if (isValid) {
      onSave(urlToValidate, currentDisplayName);
      onClose();
    }
  }, [url, displayName, onSave, onClose]);

  const handleClose = () => {
    setUrl(initialValue);
    setDisplayName(initialDisplayName);
    setUrlError("");
    setDisplayNameError("");
    onClose();
  };

  // Fallback because 2 fields in a form prevents onSubmit from being called
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        handleSave();
      }
    },
    [handleSave],
  );

  return (
    <Modal size="tiny" open={isOpen} onClose={handleClose}>
      <Modal.Header className={styles.header}>
        <ModalHeader title="Connect External Link" onClose={handleClose} />
      </Modal.Header>
      <Modal.Content className={styles.content}>
        <Form onSubmit={handleSave} onKeyDown={handleKeyDown}>
          <Form.Field error={!!urlError}>
            <label>External Link</label>
            <Input placeholder="External Link" value={url} onChange={(e) => setUrl(e.target.value)} fluid />
            {urlError && <span className="error">{urlError}</span>}
          </Form.Field>
          <Form.Field error={!!displayNameError} style={{ marginTop: "1rem" }}>
            <label>Display Name</label>
            <Input
              placeholder="Enter a display name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              fluid
              maxLength={128}
            />
            {displayNameError && <span className="error">{displayNameError}</span>}
          </Form.Field>
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button basic color="blue" onClick={handleClose} className={styles.button}>
          Cancel
        </Button>
        <Button primary onClick={handleSave} className={styles.button}>
          Connect
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default NextLinkModal;
